import React, { FC, useRef, useState, useEffect, useCallback } from 'react';

import { SnackbarNotification, SnackbarState } from 'ia-react-core/~/interfaces/SnackbarState';
import useTranslation from '../../../hooks/useTranslation';
import useScopedSelector from '../../../hooks/useScopedSelector';
import { StyledSnackbarWrapper, StyledNotification, StyledCloseButton } from './Snackbar.styles';

export interface SnackbarProps {
  /** The name of the snackbar. If provided, it will be used as the reducer key. Otherwise the reducer key will be "Snackbar${snackbarId}". */
  name?: string;
  /** Default duration of the notification before they are dismissed. */
  duration?: number;
  /** Margin from the top when the notifications are top aligned. */
  topMargin?: number;
  /** Margin from the bottom when the notifications are bottom aligned. */
  bottomMargin?: number;
}

interface SnackbarInternalProps {
  snackbarId: number;
}

export interface SnackbarComposedProps extends SnackbarProps, SnackbarInternalProps {}

const SnackbarDefaultProps = {
  duration: 2000,
  topMargin: 68,
  bottomMargin: 16,
};

/**
 * @since 1.4.0
 */
const Snackbar: FC<SnackbarComposedProps> = ({ name, duration, topMargin, bottomMargin }) => {
  const ref = useRef<HTMLDivElement>();
  const notifications: SnackbarState['notifications'] = useScopedSelector(`${name}.notifications`);
  const dismiss: SnackbarState['dismiss'] = useScopedSelector(`${name}.dismiss`);

  const currentNotificationRef = useRef<SnackbarNotification>(null);

  const [timer, setTimer] = useState<NodeJS.Timeout>();
  const [opened, setOpened] = useState(false);
  const [closing, setClosing] = useState(false);
  const [hovering, setHovering] = useState(false);

  const clearTimer = useCallback(() => {
    clearTimeout(timer);
    setTimer(undefined);
  }, [timer]);

  const handleClosed = useCallback(() => {
    ref.current?.removeEventListener('transitionend', handleClosed);
    dismiss(currentNotificationRef.current.notificationId);
    currentNotificationRef.current = null;
    setClosing(false);
  }, [dismiss]);

  const closeNotification = useCallback(() => {
    setClosing(true);
    setOpened(false);
    clearTimer();
    ref.current?.addEventListener('transitionend', handleClosed);
    if (!ref.current) handleClosed();
  }, [clearTimer, handleClosed]);

  const setClosingTimer = useCallback(() => {
    const newTimer = setTimeout(closeNotification, currentNotificationRef.current?.options?.duration || duration);
    setTimer(newTimer);
  }, [closeNotification, duration]);

  const openNotification = useCallback(() => {
    const [currentNotification] = notifications;
    currentNotificationRef.current = currentNotification;
    setTimeout(() => {
      setOpened(true);
    }, 100);
  }, [notifications]);

  const handleMouseEnter = useCallback(() => {
    clearTimer();
    setHovering(true);
  }, [clearTimer]);

  const handleMouseLeave = useCallback(() => {
    setHovering(false);
    setClosingTimer();
  }, [setClosingTimer]);

  useEffect(() => {
    if (notifications?.[0]) {
      openNotification();
    }
  }, [openNotification, notifications]);

  useEffect(() => {
    if (opened && !closing && !timer && !hovering) {
      setClosingTimer();
    }
  }, [closing, timer, hovering, setClosingTimer, opened]);

  const { t } = useTranslation();

  return (
    <StyledSnackbarWrapper>
      {notifications?.[0] && (
        <StyledNotification
          ref={ref}
          opened={opened}
          closing={closing}
          topMargin={topMargin}
          bottomMargin={bottomMargin}
          title={t(notifications?.[0].message)}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {t(notifications?.[0].message)}
          <StyledCloseButton
            white
            data-testid="SnackbarCloseNotificationButton"
            onClick={closeNotification}
          />
        </StyledNotification>
      )}
    </StyledSnackbarWrapper>
  );
};

Snackbar.defaultProps = SnackbarDefaultProps;

export default Snackbar;
